import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

// eslint-disable-next-line import/no-unresolved
import { ProfileMenu, ThemeButton } from "@astrana/ui/base";

import { useAdtBellVisible } from "./useAdtBellVisible";
import { useTopNavWorklistVisible } from "./useTopNavWorklistVisible";
import { useAuthContext } from "../auth/AuthContext";
import { isEmbeddedInExtension } from "../bridge/types";
import { UpdateMenuItem, UpdatePill } from "../lib/UpdateAvailableControls";
import { useUpdateAvailable } from "../lib/updateAvailable";
import { useDarkMode } from "../theme/useDarkMode";
import { usePersonaSwitcher } from "./usePersonaSwitcher";

// Lazy on purpose — see AdtBell.tsx's header. Importing it eagerly would pull
// `@astrana/ui/modules` into this file's static graph, which (a) makes every
// session download the notifications module even for a persona the bell is
// hidden from (see useAdtBellVisible.ts — persona-gated, no VITE flag), and
// (b) stops AppHeaderComposition.test.tsx from loading, since that bundle
// emits an extensionless nested `lucide-react/dynamic` import which Vitest's
// ESM resolver rejects.
const AdtBell = lazy(() => import("./AdtBell"));

interface Props {
  children: ReactNode;
}

// Computed once at module load — the embedded-vs-standalone state can't
// change during a page's lifetime (it's the browser's window hierarchy).
const EMBEDDED = isEmbeddedInExtension();

// Routes where the focus is on a single member — chromeless layout so
// the profile uses the whole viewport. PatientHeader carries its own
// Back / theme / profile affordances via `headerActions`.
const isProfileRoute = (pathname: string): boolean =>
  pathname.startsWith("/members/") && pathname !== "/members";

/** Account-menu children: the pending-update item (top) plus the persona switcher; null when neither. */
function useProfileMenuExtras(): ReactNode {
  const personaSwitcher = usePersonaSwitcher();
  const updatePending = useUpdateAvailable() !== null;
  if (!updatePending && !personaSwitcher) return null;
  return (
    <>
      <UpdateMenuItem />
      {personaSwitcher}
    </>
  );
}

export default function AppLayout({ children }: Props) {
  const { accountLabel, logout } = useAuthContext();
  const location = useLocation();
  const onProfileRoute = isProfileRoute(location.pathname);
  // Read unconditionally (hooks can't be conditional) — cheap synchronous
  // flag+persona check, same gate every other AdtBell mount site uses. See
  // the chromeless branch below for why this one needs its OWN call site.
  const adtBellVisible = useAdtBellVisible();

  // Profile route: chromeless — PatientHeader provides its own affordances.
  //
  // Uses `h-full` (not `min-h-full`) so the viewport height propagates
  // down to MemberProfile's `styles.container` (`h-full`). With
  // `min-h-full` the outer div has `height: auto`, and a child's
  // `height: 100%` collapses to content height, leaving a white band
  // below the module list when content is shorter than the viewport.
  // MemberProfile already manages its own internal scroll, so we don't
  // need the parent to grow.
  //
  // ADT bell (ACT-1346 follow-up): this branch renders NEITHER AppHeader NOR
  // EmbeddedControls, so — unlike every other route — there is no existing
  // call site to mount the bell from. Left as-is, a provider who navigates
  // straight into a member profile (the extension's typical single-member
  // context, or classic non-tab-shell web where a profile deep-link is the
  // common entry point) never sees the bell unless they separately visit a
  // non-profile route (Search/Worklist) that happens to render a header —
  // NOT a resolution-gate problem (`useWorklistAccessGate` already resolves
  // "ready" from a persisted NPI+IPA+PP-bearer with zero worklist interaction
  // — see useWorklistAccessGate.test.tsx), purely a "nowhere to mount it"
  // problem on this one chromeless branch. Fixed by surfacing a small fixed
  // corner affordance here, gated on the EXACT SAME `useAdtBellVisible()`
  // boundary and the SAME lazy-import/Suspense shape as every other site —
  // see this file's header comment on `AdtBell`.
  if (onProfileRoute) {
    return (
      <div className="h-full flex flex-col font-sans text-foreground bg-background">
        <main className="flex-1 min-h-0">{children}</main>
        <div className="fixed top-3 right-3 z-30 flex items-center gap-1">
          <UpdatePill />
          {adtBellVisible && (
            <Suspense fallback={null}>
              <AdtBell />
            </Suspense>
          )}
        </div>
      </div>
    );
  }

  // Embedded in the Chrome extension side panel: the extension chrome
  // (green bar with "AstraConnect" / plum icon) already identifies the
  // app, so our permanent AppHeader would be redundant branding directly
  // beneath it. Render a compact right-aligned control strip with theme
  // toggle + profile menu so those affordances stay visible (previously
  // hidden behind a three-dot dropdown).
  if (EMBEDDED) {
    return (
      <div className="min-h-full flex flex-col font-sans text-foreground bg-background">
        <EmbeddedControls
          accountLabel={accountLabel}
          onSignOut={() => void logout()}
        />
        <main className="flex-1 min-h-0">{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-full flex flex-col font-sans text-foreground bg-background">
      <StandaloneAppHeader
        accountLabel={accountLabel}
        onSignOut={() => void logout()}
      />
      <main className="flex-1 min-h-0">{children}</main>
    </div>
  );
}

/**
 * The classic (non-tab-shell) standalone header: renders the 2-tab
 * Search / Priority Worklist PrimaryNav only when the worklist is gated in
 * (mirrors today's behavior). The keep-alive TabbedAppShell renders its own
 * 3-tab nav via <AppHeader nav={...}> instead.
 */
function StandaloneAppHeader({
  accountLabel,
  onSignOut,
}: {
  accountLabel: string | null;
  onSignOut: () => void;
}) {
  const showWorklistNav = useTopNavWorklistVisible();
  return (
    <AppHeader
      accountLabel={accountLabel}
      onSignOut={onSignOut}
      nav={showWorklistNav ? <PrimaryNav /> : undefined}
    />
  );
}

/**
 * Compact right-aligned control strip for the Chrome extension side panel —
 * the extension chrome (green bar with "AstraConnect"/plum icon) already
 * identifies the app, so a full `AppHeader` wordmark would be redundant.
 *
 * P6d — exported (was module-private) and given an optional `nav` slot so
 * TabbedAppShell can compose its OWN tab bar into this SAME embedded strip
 * when running in the extension (rather than duplicating embedded-chrome
 * logic or falling back to the full standalone `AppHeader`, which doesn't
 * belong inside the extension's own branded strip). `nav` sits in a
 * `flex-1 min-w-0` region so the responsive/icon-condensed tab bar can
 * actually shrink to the narrow side-panel width; omitted (as by AppLayout's
 * own embedded branch above) it collapses to zero width, an unchanged
 * `justify-end`-equivalent layout for that call site.
 *
 * ADT bell: mounted here identically to `AppHeader` below — same
 * `useAdtBellVisible()` gate, same `Suspense`-wrapped lazy `AdtBell` (see
 * `AdtBell.tsx`'s header for why the gate must be read BEFORE the lazy
 * import, not inside it). There is no separate/looser embedded gate; a
 * provider using the extension side panel gets the exact same panel-wide
 * admit/discharge feed a standalone-web provider does, and a click behaves
 * identically too (`AdtBell`'s `handleSelectMember` navigates to
 * `/members/astrana:<id>` and fires the discharge-focus signal — unchanged
 * by embedding).
 */
export function EmbeddedControls({
  accountLabel,
  onSignOut,
  nav,
}: {
  accountLabel: string | null;
  onSignOut: () => void;
  nav?: ReactNode;
}) {
  const { isDark, toggleTheme } = useDarkMode();
  const personaSwitcher = useProfileMenuExtras();
  // Cheap, synchronous (flag + persona module) — see AppHeader's identical
  // call below for why the RENDER is gated here rather than inside AdtBell.
  const adtBellVisible = useAdtBellVisible();
  return (
    <div className="app-header-container flex items-center justify-between gap-2 px-3 py-2 border-b border-border shrink-0 relative z-20">
      <div className="min-w-0 flex-1 overflow-hidden">{nav}</div>
      <div className="flex items-center gap-1 shrink-0">
        {/* `ThemeButton` is a library component (no className to reach its
            internal button) — wrap it so the whole toggle can still be
            hidden on the narrow side panel via the same @container rule. */}
        <UpdatePill />
        <span className="app-header-theme-toggle">
          <ThemeButton isDark={isDark} onToggle={toggleTheme} />
        </span>
        {adtBellVisible && (
          <Suspense fallback={null}>
            <AdtBell />
          </Suspense>
        )}
        {/* Always render while authenticated — some B2C tokens carry no
            readable label, and hiding the menu removes the only Sign out.
            Persona switching (multi-persona users only) lives inside the menu;
            `personaSwitcher` is null otherwise, so no empty section renders. */}
        <ProfileMenu label={accountLabel ?? "Account"} onSignOut={onSignOut}>
          {personaSwitcher}
        </ProfileMenu>
      </div>
    </div>
  );
}

/**
 * Shared standalone-web header chrome (logo wordmark + optional primary nav
 * slot + theme toggle + account/persona menu). The `nav` slot lets callers
 * inject their own primary navigation: AppLayout injects the classic 2-tab
 * PrimaryNav; the keep-alive TabbedAppShell injects its 3-tab nav.
 */
export function AppHeader({
  accountLabel,
  onSignOut,
  nav,
}: {
  accountLabel: string | null;
  onSignOut: () => void;
  nav?: ReactNode;
}) {
  const [mounted, setMounted] = useState(false);
  const { isDark } = useDarkMode();
  const personaSwitcher = useProfileMenuExtras();
  // Cheap, synchronous (flag + persona module). Gating the RENDER here rather
  // than inside AdtBell keeps the lazy chunk — and its EntitledIpas fetch —
  // from loading at all when the feature is off, which is the default.
  const adtBellVisible = useAdtBellVisible();
  const symbol = isDark
    ? "/images/logo_A_white.svg"
    : "/images/logo_A_plum.svg";
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  return (
    <header
      className={
        // `relative z-20` keeps this header (and, critically, ProfileMenu's
        // INLINE, non-portaled sign-out dropdown, which opens downward into
        // the content region) painting ABOVE the sibling <main>. TabbedAppShell
        // gives its <main> `position: relative`, which would otherwise paint
        // over the header's stacking context and swallow clicks on the dropdown.
        "app-header-container flex items-center justify-between gap-4 px-4 py-2.5 relative z-20 " +
        "bg-card border-b border-border shadow-sm shrink-0 " +
        "transition-[transform,opacity] duration-300 ease-out " +
        (mounted ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0")
      }
    >
      {/* `flex-1` is REQUIRED, not cosmetic: the nav slot below wraps a
          `container-type: inline-size` element (TabShellNav), which contributes
          ZERO intrinsic inline width. Without `flex-1` here this group is
          content-sized (just the logo) and the `flex-1` nav wrapper collapses
          to 0 width — the tab bar renders into the DOM but is invisible. */}
      <div className="flex items-center gap-4 min-w-0 flex-1">
        {/* The brand mark is the way Home. It replaced an explicit Home tab
            in TabShellNav (see TabbedAppShell's `fixedTabs`) — a logo that
            goes home is the convention users already try first, and it costs
            the header row no horizontal space, which is the whole reason the
            tab bar was crowded. `aria-label` on the link because the visible
            wordmark beside it disappears at narrow widths via
            `.app-header-collapsible-label`, leaving an image-only link. */}
        <Link
          to="/home"
          aria-label="Atlas Copilot — go to Home"
          className="flex items-center gap-1 shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div
            style={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 9999,
              background: isDark
                ? "linear-gradient(to bottom, rgba(198, 107, 162, 0.25), transparent)"
                : "linear-gradient(to bottom, rgba(108, 12, 70, 0.12), transparent)",
            }}
          >
            <img
              src={symbol}
              alt="Astrana Health Logo"
              style={{ width: 16 }}
            />
          </div>
          {/* Brand collapse (narrow side-panel width): only the "A" mark
              above stays visible; the wordmark text hides via the shared
              `.app-header-collapsible-label` @container rule so the nav/
              search/PPI/patient-tab icons keep their row space instead of
              being pushed off. */}
          <h1 className="app-header-collapsible-label m-0 text-lg font-bold whitespace-nowrap bg-gradient-to-r from-primary to-[hsl(324,60%,45%)] dark:from-[hsl(324,60%,70%)] dark:to-[hsl(324,80%,82%)] bg-clip-text text-transparent">
            Atlas Copilot
          </h1>
        </Link>
        {/* Top-level nav slot — standalone web (AppLayout injects the classic
            2-tab PrimaryNav, gated on flag+persona; the keep-alive
            TabbedAppShell injects its own responsive tab bar) AND, as of P6d,
            the extension side panel too (TabbedAppShell composes its tab bar
            into EmbeddedControls' own `nav` slot there instead of this
            header — see EmbeddedControls above). `min-w-0` lets the nav
            region actually shrink within this row (and, via its own
            `@container`, condense to icon-only) instead of forcing the whole
            header wider than the viewport. */}
        {nav && <div className="min-w-0 flex-1 overflow-hidden">{nav}</div>}
      </div>
      {/* UTILITIES — deliberately a different control family from the nav on
          the left. The nav are tabs: rectangular, tinted, content-weight. These
          are ambient controls (theme, alerts, account), so they read as
          circular ghost glyphs at muted weight instead. Shape is doing the
          separating here, which is why the theme toggle is `rounded-full`
          rather than the `rounded-md` it used to be.

          `gap-1`, not `gap-2`: every child below now occupies the SAME 36px
          square, so each glyph already carries ~10px of its own breathing
          room. The old `gap-2` sat between boxes of three different sizes
          (28px toggle, 36px bell, 28px avatar), so equal box gaps produced
          visibly UNEQUAL optical gaps — the spacing complaint. Equal cells are
          what make one gap value read evenly. */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Closes the utility group so it reads as a cluster rather than as
            more nav that happens to be right-aligned. */}
        <span aria-hidden="true" className="mr-1 h-5 w-px bg-border" />
        <UpdatePill />
        <ThemeToggleButton />
        {/* OPTICAL centring, not geometric. All three cells here are 36px on a
            `gap-1` row, so the three icon CENTRES are already evenly spaced at
            40px. They do not LOOK evenly spaced, because the neighbours have
            very different visual mass: the moon and bell are ~16px glyphs
            (half-mass ~8px) while the avatar is a 28px filled disc
            (half-mass 14px). Measured as visible whitespace:

              moon -> bell   40 - 8 - 8  = 24px
              bell -> avatar 40 - 8 - 14 = 18px

            so the bell reads as belonging to the avatar. The -3px/+3px pair
            below shifts it left by 3 to land both gaps at 21px. It nets to
            ZERO layout width, so the avatar and the header's right edge do not
            move — this only re-centres the bell inside the space it already
            had.

            Margins rather than `relative`/`translate-x`: the bell opens a
            Radix Popover, and a transform would make this wrapper a containing
            block for positioned descendants.

            The wrapper also sits OUTSIDE `Suspense` so the 36px cell is
            reserved while the lazy chunk loads, instead of the row reflowing
            when the bell appears. */}
        {adtBellVisible && (
          <span className="-ml-[3px] mr-[3px] flex h-9 w-9 items-center justify-center">
            <Suspense fallback={null}>
              <AdtBell />
            </Suspense>
          </span>
        )}
        {/* Unified account menu (matches the extension). Sign out lives inside;
            multi-persona users also get a persona switcher here (null otherwise).

            Wrapped in a 36px cell rather than resized: the avatar is a library
            component whose own trigger is `w-7 h-7`, and stretching that would
            inflate its filled circle. Giving it a matching CELL keeps the 28px
            disc but puts its centre on the same rhythm as the 36px toggle and
            bell beside it. The wrapper is deliberately NOT `relative` — the
            sign-out dropdown is inline and absolutely positioned, so adding a
            positioned ancestor here would re-anchor it. */}
        <span className="flex h-9 w-9 items-center justify-center">
          <ProfileMenu label={accountLabel ?? "Account"} onSignOut={onSignOut}>
            {personaSwitcher}
          </ProfileMenu>
        </span>
      </div>
    </header>
  );
}



// Moved to its own module (useTopNavWorklistVisible.ts, imported above) so
// useAdtBellVisible.ts can depend on it without a circular
// AppLayout<->useAdtBellVisible import. Re-exported here so existing call
// sites (TabbedAppShell.tsx) are unaffected.
export { useTopNavWorklistVisible };

/**
 * Minimal top-level nav — standalone web only. Priority Worklist ("Patient
 * Panel") / Hospital-ED; only rendered once the worklist is gated in (see
 * StandaloneAppHeader's `showWorklistNav` check above, which this doesn't
 * change — Hospital/ED shares that SAME gate, so it never appears alone).
 * Home and Search used to lead this list; with them gone, a session without
 * the worklist gate renders no nav at all and keeps the chromeless
 * single-title header, which is the same outcome as before.
 */
function PrimaryNav() {
  const location = useLocation();
  const isWorklist = location.pathname.startsWith("/worklist");
  const isHospitalEd = location.pathname.startsWith("/hospital-ed");
  // Home and Search tabs removed here too, to keep classic mode consistent
  // with the shell: the logo goes Home (see `AppHeader`) and search lives in
  // a Home widget. Both routes are untouched — only these buttons are gone.
  // This nav only renders at all once the worklist is gated in
  // (`showWorklistNav` in StandaloneAppHeader), so dropping two of four tabs
  // can't leave an empty nav box; without that gate there is no nav.
  return (
    <nav aria-label="Primary" className="flex items-center gap-1">
      <NavTab to="/worklist" label="Patient Panel" active={isWorklist} />
      <NavTab to="/hospital-ed" label="Hospital/ED" active={isHospitalEd} />
    </nav>
  );
}

export function NavTab({
  to,
  label,
  active,
  state,
}: {
  to: string;
  label: string;
  active: boolean;
  state?: unknown;
}) {
  return (
    <Link
      to={to}
      state={state}
      className={
        "px-3 py-1.5 rounded-md text-sm font-medium transition-colors " +
        (active
          ? "bg-accent text-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-accent")
      }
    >
      {label}
    </Link>
  );
}

function ThemeToggleButton() {
  const { isDark, toggleTheme } = useDarkMode();
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      // `h-9 w-9 rounded-full` matches AdtNotifications' own bell trigger
      // (NotificationBell.tsx) exactly, so the two utility glyphs sit on one
      // rhythm instead of a 28px square beside a 36px circle. Circular is also
      // the shape half of the nav-vs-utility split described in AppHeader.
      className="app-header-theme-toggle h-9 w-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
    >
      <ThemeIcon isDark={isDark} />
    </button>
  );
}

function ThemeIcon({ isDark }: { isDark: boolean }) {
  // Shows sun in dark mode (click to go light) and moon in light mode
  // (click to go dark). Matches PP and most OS toggles.
  if (isDark) {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
  }
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
